import React from "react";
import {
    Card,
    CardContent,
    Avatar,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Button,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import image from "../../../helper/image";
import "./BookingLineupCards.css";

const PlayerCard = ({
    lineup,
    index,
    statusText,
    feeDisplay,
    superFeeDisplay,
    invoiceAmountDisplay,
    editMode,
    manualFee,
    currencySymbol,
    onEdit,
    onFeeChange,
    onDone,
    onCancel,
    onRemove,
    onExpand,
    rosters,
    onSelectPlayer,
    allLineupProfileIds,
    bookingStatus,
    isActAdmin,
    isPayByActOwner,
    showRemoveButton,
    showEditButton,
    isBookingCancelled,
    isBookingNew,
    extraGigFee
}) => {
    const playerExtraGigFeeString = extraGigFee > 0 
        ? ` (+ ${currencySymbol} ${extraGigFee})`
        : "";

    const getStatusClassName = () => {
        const status = statusText?.toLowerCase() || "";
        if (status.includes("accepted")) return "status-accepted";
        if (status.includes("invited") && !status.includes("not")) return "status-invited";
        if (status.includes("not yet") || status.includes("not invited")) return "status-not-invited";
        if (status.includes("declined")) return "status-declined";
        return "";
    };

    const isPlayerDisabled = (player) => {
        return (
            isBookingCancelled ||
            isBookingNew ||
            !player.available ||
            allLineupProfileIds?.includes(player.profile?.id)
        );
    };

    const hasRosterPlayers = rosters && rosters.length > 0;

    return (
        <div className="lineup-player-card">
            <Card elevation={1}>
                <div className="lineup-player-card-header">
                    <Avatar 
                        className="lineup-player-card-avatar" 
                        src={lineup?.profile ? image.getProfileImage(lineup.profile) : undefined}
                        alt={lineup?.profile ? `${lineup.profile.firstName} ${lineup.profile.lastName}` : "Player"}
                    >
                        {lineup?.profile ? `${lineup.profile.firstName?.[0] || ""}${lineup.profile.lastName?.[0] || ""}` : "?"}
                    </Avatar>
                    <div className="lineup-player-card-info">
                        {lineup?.profile && (
                            <Typography variant="subtitle1" className="lineup-player-card-name">
                                {lineup.profile.firstName} {lineup.profile.lastName}
                            </Typography>
                        )}
                        {lineup && (
                            <Typography variant="caption" className="lineup-player-card-instrument">
                                {lineup.name}
                            </Typography>
                        )}
                    </div>
                </div>

                <CardContent className="lineup-player-card-content">
                    <div className="lineup-player-card-details">
                        <div className="lineup-player-card-row">
                            <span className="lineup-player-card-label">
                                {isActAdmin && isPayByActOwner ? "Performance Fee" : "Player Fee"}
                            </span>
                            <span className="lineup-player-card-value">
                                {editMode ? (
                                    <TextField
                                        value={manualFee !== undefined ? manualFee : lineup?.fee}
                                        onChange={(e) => onFeeChange(e.target.value)}
                                        type="text"
                                        size="small"
                                        className="lineup-player-card-fee-input"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {currencySymbol}
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                ) : (
                                    `${feeDisplay}${playerExtraGigFeeString}`
                                )}
                            </span>
                        </div>

                        <div className="lineup-player-card-row">
                            <span className="lineup-player-card-label">Super Fee</span>
                            <span className="lineup-player-card-value">{superFeeDisplay || "-"}</span>
                        </div>
                        <div className="lineup-player-card-row">
                            <span className="lineup-player-card-label">Invoice Amount</span>
                            <span className="lineup-player-card-value">{invoiceAmountDisplay || "-"}</span>
                        </div>

                        <div className="lineup-player-card-row">
                            <span className="lineup-player-card-label">Status</span>
                            <span className={`lineup-player-card-status-chip ${getStatusClassName()}`}>
                                {statusText}
                            </span>
                        </div>
                    </div>

                    {editMode && (
                        <div className="lineup-player-card-edit-actions">
                            <Button 
                                variant="contained" 
                                color="primary" 
                                size="small"
                                onClick={onDone}
                                className="lineup-player-card-done-btn"
                            >
                                Done
                            </Button>
                            <Button 
                                variant="outlined" 
                                size="small"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </CardContent>

                <div className="lineup-player-card-footer">
                    <div className="lineup-player-card-footer-actions">
                        {hasRosterPlayers && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onExpand}
                                className={`lineup-player-card-change-btn ${lineup?.expanded ? 'expanded' : ''}`}
                                disabled={isBookingCancelled}
                            >
                                Change Player
                                <ExpandMoreIcon 
                                    className={`lineup-player-card-expand-icon ${lineup?.expanded ? 'rotate-180' : ''}`} 
                                    style={{ marginLeft: 4, fontSize: 18 }}
                                />
                            </Button>
                        )}

                        {!editMode && showEditButton && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onEdit}
                                className="lineup-player-card-edit-btn"
                            >
                                Edit Fee
                            </Button>
                        )}

                        {showRemoveButton && (
                            <IconButton 
                                onClick={onRemove}
                                className="lineup-player-card-remove-btn"
                                disabled={isBookingCancelled || isBookingNew}
                                size="small"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </div>
                </div>

                {hasRosterPlayers && (
                    <Collapse in={lineup?.expanded} timeout="auto">
                        <CardContent className="lineup-player-card-roster">
                            <Typography variant="body2" gutterBottom className="lineup-player-card-roster-title">
                                Select player:
                            </Typography>
                            <List dense>
                                {rosters.map((player) => (
                                    player && player.profile ? (
                                        <ListItem
                                            key={player.id}
                                            dense
                                            button
                                            onClick={() => onSelectPlayer(player)}
                                            disabled={isPlayerDisabled(player)}
                                            className="lineup-player-card-roster-item"
                                        >
                                            <Avatar 
                                                src={image.getProfileImage(player.profile)} 
                                                alt={player.profile.firstName}
                                                className="lineup-player-card-roster-avatar"
                                            />
                                            <ListItemText
                                                primary={`${player.profile.firstName} ${player.profile.lastName}`}
                                                secondary={player.available ? "Available" : "Unavailable"}
                                            />
                                            <ListItemSecondaryAction>
                                                <Checkbox
                                                    onChange={() => onSelectPlayer(player)}
                                                    checked={player.profileId === lineup?.profileId}
                                                    disabled={isPlayerDisabled(player)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ) : null
                                ))}
                            </List>
                        </CardContent>
                    </Collapse>
                )}
            </Card>
        </div>
    );
};

export default PlayerCard;
